import * as React from 'react';
import { useId } from 'react';
import type { SVGProps } from 'react';

// Marca de «Rumbo Finanzas»: «R» caligráfica cuya asta es la aguja de una
// brújula, dentro de un anillo con las cuatro puntas cardinales. Geometría y
// paleta en docs-vadym/marca.md (fuente: docs-vadym/generar-iconos.mjs).
//
// Colores: --p-logo-ink (R y puntas), --p-logo-ring (anillo) y
// --p-logo-accent (media aguja y media punta), definidos por los temas
// «Rumbo»; en otros temas, la R toma currentColor y el anillo el verde.

const ANILLO = 'M353.7 372.4 A152 152 0 1 1 396.9 312.9';
const PUNTA_A = 'M256 34 L238 106 L256 122 Z';
const PUNTA_B = 'M256 34 L274 106 L256 122 Z';
const MARCAS =
  'M172.6 339.4 L164.8 347.2 M172.6 172.6 L164.8 164.8 M339.4 172.6 L347.2 164.8';
const AGUJA_A = 'M170 345 L282 190 L202.6 262.4 Z';
const AGUJA_B = 'M170 345 L282 190 L238.2 288.1 Z';
const PANZA =
  'M148 217 C184 185 240 163 292 163 C337 163 362 187 360 219 C358 252 318 278 256 288 L250 266 C300 258 322 240 324 218 C326 197 311 188 288 189 C246 190 196 201 148 217 Z';
const PIERNA =
  'M246 270 C296 270 320 298 338 326 C354 350 378 364 422 342 C394 384 338 388 310 354 C294 334 282 302 250 288 Z';

// Variante simplificada (≤ 32 px): R gruesa + anillo, sin puntas ni marcas.
const ANILLO_SIMPLE = 'M376.7 410.5 A196 196 0 1 1 442.4 316.6';
const AGUJA_SIMPLE = 'M156 392 L178.5 280.1 L284 168 L241 315.8 Z';
const PANZA_SIMPLE =
  'M176 196 C236 156 304 150 336 164 C376 182 372 248 300 268 L262 274';
const PIERNA_SIMPLE =
  'M266 276 C316 284 330 330 352 356 C372 380 400 384 436 370';

const INK = 'var(--p-logo-ink, currentColor)';
const RING = 'var(--p-logo-ring, #2e8b62)';
const ACC = 'var(--p-logo-accent, var(--p-logo-ring, #2e8b62))';

type SvgLogoProps = SVGProps<SVGSVGElement> & {
  /** «simple» para tamaños de 32 px o menos. */
  variante?: 'completa' | 'simple';
};

export const SvgLogo = ({ variante = 'completa', ...props }: SvgLogoProps) => {
  const mascara = `eje-${useId().replace(/:/g, '')}`;
  return (
    <svg
      role="img"
      aria-label="Rumbo Finanzas"
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 512 512"
      style={{
        color: 'inherit',
        ...props.style,
      }}
    >
      {variante === 'simple' ? (
        <>
          <path
            d={ANILLO_SIMPLE}
            stroke={RING}
            strokeWidth={48}
            strokeLinecap="round"
          />
          <path d={AGUJA_SIMPLE} fill={INK} />
          <path
            d={PANZA_SIMPLE}
            stroke={INK}
            strokeWidth={46}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d={PIERNA_SIMPLE}
            stroke={INK}
            strokeWidth={46}
            strokeLinecap="round"
          />
        </>
      ) : (
        <>
          <defs>
            <mask id={mascara}>
              <rect width="512" height="512" fill="#fff" />
              <circle cx="220.4" cy="275.3" r="7" fill="#000" />
            </mask>
          </defs>
          <path
            d={ANILLO}
            stroke={RING}
            strokeWidth={24}
            strokeLinecap="round"
          />
          {[0, 90, 180, 270].map(r => (
            <g key={r} transform={`rotate(${r} 256 256)`}>
              <path d={PUNTA_A} fill={INK} />
              <path d={PUNTA_B} fill={ACC} />
            </g>
          ))}
          <path
            d={MARCAS}
            stroke={RING}
            strokeWidth={5}
            strokeLinecap="round"
          />
          <g mask={`url(#${mascara})`}>
            <path d={AGUJA_A} fill={ACC} />
            <path d={AGUJA_B} fill={INK} />
          </g>
          <path d={PANZA} fill={INK} />
          <path d={PIERNA} fill={INK} />
        </>
      )}
    </svg>
  );
};

/** Barco con ola: ornamento discreto de la marca (monocromo). */
export const SvgBarco = (props: SVGProps<SVGSVGElement>) => (
  <svg
    aria-hidden="true"
    {...props}
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 120 40"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.6}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M60 6 V24" />
    <path d="M60 6 L66 8.2 L60 10.4" fill="currentColor" />
    <path d="M62 11 L71 23 H62 Z" />
    <path d="M58 13 L51 23 H58 Z" />
    <path d="M49 26 H72 L68.5 30.5 H52.5 Z" />
    <path d="M2 34 Q10 30 18 34 T34 34 T50 34 T66 34 T82 34 T98 34 T118 34" />
  </svg>
);
